import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Send, MessageCircle, Phone, Mail, Globe } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "Food Safety Audit",
  "Kitchen Hygiene Check",
  "Compliance Support",
  "FSSAI License Support",
  "Multiple Services",
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [form, setForm] = useState({ name: "", phone: "", business: "", email: "", service: "Food Safety Audit", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/inquiries`, form);
      setSubmitted(true);
      setForm({ name: "", phone: "", business: "", email: "", service: "Food Safety Audit", message: "" });
    } catch {
      setError("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 md:py-20" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid lg:grid-cols-5 gap-10 md:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3"
          >
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-4 block">Get In Touch</span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold text-[#16231A] tracking-tight leading-[1.2] mb-4">
              Let's Improve Your Food Safety Standards
            </h2>
            <p className="text-[#526B57] mb-8 leading-relaxed max-w-lg">
              Whether you want a single outlet hygiene check, a structured audit program or support in getting your FSSAI license, we can help.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#EAF0E2] border border-[#7CA63A]/30 rounded-2xl p-8 text-center"
                data-testid="inquiry-success"
              >
                <div className="w-14 h-14 bg-[#7CA63A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send size={22} className="text-white" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-[#16231A] mb-2">Inquiry Sent!</h3>
                <p className="text-[#526B57] text-sm">We'll get back to you within 24 hours. For urgent queries, chat with us on WhatsApp.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-4 text-sm text-[#7CA63A] font-medium hover:underline"
                  data-testid="send-another-btn"
                >
                  Send another inquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="form-error">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Your Name *</label>
                    <input
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                      data-testid="inquiry-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Phone Number *</label>
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                      data-testid="inquiry-phone-input"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Business Name</label>
                    <input
                      name="business"
                      type="text"
                      value={form.business}
                      onChange={handleChange}
                      placeholder="Restaurant, cafe, bakery name"
                      className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                      data-testid="inquiry-business-input"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Email Address</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                      data-testid="inquiry-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Service Required</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                    data-testid="inquiry-service-select"
                  >
                    {serviceOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your requirement, outlet type, city and number of locations."
                    className="w-full px-4 py-3 bg-white border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors resize-none"
                    data-testid="inquiry-message-input"
                  />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#7CA63A] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#68902A] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 flex items-center gap-2"
                    data-testid="submit-inquiry-button"
                  >
                    <Send size={16} />
                    {submitting ? "Sending..." : "Send Inquiry"}
                  </button>
                  <a
                    href="https://wa.me/918976254720?text=Hello%20Food%20Safety%20Partners%2C%20I%20would%20like%20to%20know%20more%20about%20your%20food%20safety%20services."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#25D366] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#1EB554] transition-all duration-300 flex items-center gap-2"
                    data-testid="whatsapp-cta-btn"
                  >
                    <MessageCircle size={16} />
                    Chat on WhatsApp
                  </a>
                </div>
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-white border border-[#E3E8DC] rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center">
                  <Mail size={18} className="text-[#7CA63A]" />
                </div>
                <div>
                  <p className="text-xs text-[#526B57]">Email</p>
                  <p className="text-sm font-medium text-[#16231A]">info@foodsafetypartners.in</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E3E8DC] rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center">
                  <Phone size={18} className="text-[#7CA63A]" />
                </div>
                <div>
                  <p className="text-xs text-[#526B57]">WhatsApp</p>
                  <p className="text-sm font-medium text-[#16231A]">+91 8976254720</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E3E8DC] rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center">
                  <Globe size={18} className="text-[#7CA63A]" />
                </div>
                <div>
                  <p className="text-xs text-[#526B57]">Website</p>
                  <p className="text-sm font-medium text-[#16231A]">www.foodsafetypartners.in</p>
                </div>
              </div>
            </div>

            <div className="bg-[#EAF0E2] border border-[#E3E8DC] rounded-2xl p-6">
              <h4 className="font-['Outfit'] text-sm font-bold text-[#2B4131] mb-3">Service Coverage</h4>
              <div className="flex flex-wrap gap-2">
                {["Restaurants", "Cafes", "Cloud Kitchens", "Bakeries", "Manufacturing Units", "Other Establishments"].map((t) => (
                  <span key={t} className="text-xs bg-white text-[#2B4131] px-3 py-1.5 rounded-full border border-[#E3E8DC]">{t}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
